import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Heading,
  HStack,
  SimpleGrid,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Tooltip,
  Flex,
  useToast,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import CodesetSelect from "./CodesetSelect";
import UuidPreview from "./UuidPreview";
import ReferencesEditor from "./ReferencesEditor";
import {
  HiOutlineArrowNarrowLeft,
  HiOutlinePlus,
  HiOutlineSave,
} from "react-icons/hi";
import LocationsEditor from "./LocationsEditor";

// Import our new extracted components
import PremiumsEditor from "./components/placement/PremiumsEditor";
import PeriodClassesEditor from "./components/placement/PeriodClassesEditor";
import PeriodPerilsEditor from "./components/placement/PeriodPerilsEditor";
import { CoveragesEditor, DeductiblesEditor } from "./components/placement/CoveragesDeductiblesEditor";
import TaxesEditor from "./components/placement/TaxesEditor";
import CommissionsEditor from "./components/placement/CommissionsEditor";
import OtherDeductionsEditor from "./components/placement/OtherDeductionsEditor";
import BrokerageEditor from "./components/placement/BrokerageEditor";
import ReinstatementsEditor from "./components/placement/ReinstatementsEditor";
import ContractPartiesEditor from "./components/placement/ContractPartiesEditor";
import ContractMarketEditor from "./components/placement/ContractMarketEditor";
import ClausesEditor from "./components/placement/ClausesEditor";
import SchedulesEditor from "./components/placement/SchedulesEditor";
import RegulatoryProvisionsEditor from "./components/placement/RegulatoryProvisionsEditor";
import PremiumAdjustmentEditor from "./components/placement/PremiumAdjustmentEditor";

// Import hooks
import { usePlacementReducer, PLACEMENT_ACTIONS } from "./hooks/usePlacementReducer";
import { useSectionEditor } from "./hooks/useSectionEditor";
import { errorFromResponse } from "./utils/apiErrors";
import { isPlacementSupported, readPlacement, withPlacement } from "./utils/contractData";

// Period Header Component
const PeriodHeaderEditor = ({ section, sectionEditor }) => {
  const { updateField } = sectionEditor;

  return (
    <Box borderWidth="1px" borderRadius="md" p={4} borderColor="gray.500">
      <Heading size="md" mb={4}>Period Header</Heading>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
        <FormControl>
          <FormLabel>ID</FormLabel>
          <Input
            value={section.period?.id || ""}
            onChange={(e) =>
              updateField("period", { ...section.period, id: e.target.value })
            }
          />
        </FormControl>

        <CodesetSelect
          label="Status Code"
          codesetCode="7491"
          value={
            section.period?.statusCode
              ? { code: section.period.statusCode, description: "" }
              : null
          }
          onChange={(value) =>
            updateField("period", {
              ...section.period,
              statusCode: value.code,
            })
          }
        />
      </SimpleGrid>

      <FormControl mb={4}>
        <FormLabel>Description</FormLabel>
        <Input
          value={section.period?.description || ""}
          onChange={(e) =>
            updateField("period", {
              ...section.period,
              description: e.target.value,
            })
          }
        />
      </FormControl>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
        <FormControl>
          <FormLabel>Start Date/Time</FormLabel>
          <Input
            type="datetime-local"
            value={section.period?.startDateTime?.value || ""}
            onChange={(e) =>
              updateField("period", {
                ...section.period,
                startDateTime: {
                  ...section.period?.startDateTime,
                  value: e.target.value,
                },
              })
            }
          />
        </FormControl>

        <FormControl>
          <FormLabel>End Date/Time</FormLabel>
          <Input
            type="datetime-local"
            value={section.period?.endDateTime?.value || ""}
            onChange={(e) =>
              updateField("period", {
                ...section.period,
                endDateTime: {
                  ...section.period?.endDateTime,
                  value: e.target.value,
                },
              })
            }
          />
        </FormControl>
      </SimpleGrid>

      <FormControl>
        <FormLabel>Underwriting Year</FormLabel>
        <Input
          value={section.period?.underwritingYear || ""}
          onChange={(e) =>
            updateField("period", {
              ...section.period,
              underwritingYear: e.target.value,
            })
          }
        />
      </FormControl>
    </Box>
  );
};

// Section Header Component
const SectionHeaderEditor = ({ section, sectionEditor }) => {
  const { updateField } = sectionEditor;

  return (
    <Box borderWidth="1px" borderRadius="md" p={4} borderColor="gray.500">
      <UuidPreview uuid={section._id} />
      <Heading size="md" mb={4}>Header</Heading>

      <CodesetSelect
        label="Reporting Level"
        codesetCode="7495"
        value={
          section.contractReportingLevelCode
            ? { code: section.contractReportingLevelCode, description: "" }
            : null
        }
        onChange={(value) => updateField("contractReportingLevelCode", value.code)}
      />

      <FormControl mb={4}>
        <FormLabel>Name</FormLabel>
        <Input
          value={section.name || ""}
          onChange={(e) => updateField("name", e.target.value)}
        />
      </FormControl>

      <FormControl mb={4}>
        <FormLabel>Description</FormLabel>
        <Input
          value={section.description || ""}
          onChange={(e) => updateField("description", e.target.value)}
        />
      </FormControl>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
        <CodesetSelect
          label="Type"
          codesetCode="7492"
          value={section.type ? { code: section.type, description: "" } : null}
          onChange={(value) => updateField("type", value.code)}
        />

        <CodesetSelect
          label="Nature Code"
          codesetCode="7494"
          value={
            section.natureCode
              ? { code: section.natureCode, description: "" }
              : null
          }
          onChange={(value) => updateField("natureCode", value.code)}
        />
      </SimpleGrid>
    </Box>
  );
};

// Main Section Editor - wraps all the tabs for a section
const SectionEditor = ({ section, sectionIndex, dispatch }) => {
  const sectionEditor = useSectionEditor(sectionIndex, dispatch);

  // Helper to update period arrays
  const updatePeriodArray = (arrayField, newArray) => {
    sectionEditor.updateField("period", {
      ...section.period,
      [arrayField]: newArray,
    });
  };

  return (
    <Tabs orientation="vertical" variant="line" colorScheme="linkedin">
      <TabList>
        <Tab>Header</Tab>
        <Tab>Classes</Tab>
        <Tab>Locations</Tab>
        <Tab>Perils</Tab>
        <Tab>Coverages</Tab>
        <Tab>Deductibles</Tab>
        <Tab>Premiums</Tab>
        <Tab>Premium Adjustment</Tab>
        <Tab>Taxes</Tab>
        <Tab>Commissions</Tab>
        <Tab>Other Deductions</Tab>
        <Tab>Brokerage</Tab>
        <Tab>Reinstatements</Tab>
        <Tab>Contract Parties</Tab>
        <Tab>Contract Market</Tab>
        <Tab>Clauses</Tab>
        <Tab>Schedules</Tab>
        <Tab>Regulatory Provisions</Tab>
      </TabList>

      <TabPanels>
        {/* Header Tab */}
        <TabPanel>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <SectionHeaderEditor
              section={section}
              sectionEditor={sectionEditor}
            />
            <PeriodHeaderEditor
              section={section}
              sectionEditor={sectionEditor}
            />
          </SimpleGrid>
        </TabPanel>

        {/* Classes Tab */}
        <TabPanel>
          <PeriodClassesEditor
            classes={section.period?.classes || []}
            onChange={(classes) => updatePeriodArray("classes", classes)}
          />
        </TabPanel>

        {/* Locations Tab */}
        <TabPanel>
          <Heading size="md" mb={4}>Locations</Heading>
          <LocationsEditor
            filter="#Period"
            locations={section.period?.locations || []}
            onChange={(locations) => updatePeriodArray("locations", locations)}
          />
        </TabPanel>

        {/* Perils Tab */}
        <TabPanel>
          <PeriodPerilsEditor
            perils={section.period?.perils || []}
            onChange={(perils) => updatePeriodArray("perils", perils)}
          />
        </TabPanel>

        {/* Coverages Tab */}
        <TabPanel>
          <CoveragesEditor
            coverages={section.period?.coverages || []}
            onChange={(coverages) => updatePeriodArray("coverages", coverages)}
          />
        </TabPanel>

        {/* Deductibles Tab */}
        <TabPanel>
          <DeductiblesEditor
            deductibles={section.period?.deductibles || []}
            onChange={(deductibles) => updatePeriodArray("deductibles", deductibles)}
          />
        </TabPanel>

        {/* Premiums Tab */}
        <TabPanel>
          <PremiumsEditor
            premiums={section.premiums || []}
            onChange={(premiums) => sectionEditor.updateField("premiums", premiums)}
          />
        </TabPanel>

        {/* Premium Adjustment Tab */}
        <TabPanel>
          <PremiumAdjustmentEditor
            premiumAdjustment={section.premiumAdjustment || {}}
            onChange={(premiumAdjustment) =>
              sectionEditor.updateField("premiumAdjustment", premiumAdjustment)
            }
          />
        </TabPanel>

        {/* Taxes Tab */}
        <TabPanel>
          <TaxesEditor
            taxes={section.taxes || []}
            onChange={(taxes) => sectionEditor.updateField("taxes", taxes)}
          />
        </TabPanel>

        {/* Commissions Tab */}
        <TabPanel>
          <CommissionsEditor
            commissions={section.commissions || []}
            onChange={(commissions) => sectionEditor.updateField("commissions", commissions)}
          />
        </TabPanel>

        {/* Other Deductions Tab */}
        <TabPanel>
          <OtherDeductionsEditor
            otherDeductions={section.otherDeductions || []}
            onChange={(otherDeductions) =>
              sectionEditor.updateField("otherDeductions", otherDeductions)
            }
          />
        </TabPanel>

        {/* Brokerage Tab */}
        <TabPanel>
          <BrokerageEditor
            brokerages={section.brokerages || []}
            onChange={(brokerages) => sectionEditor.updateField("brokerages", brokerages)}
          />
        </TabPanel>

        {/* Reinstatements Tab */}
        <TabPanel>
          <ReinstatementsEditor
            reinstatements={section.reinstatements || []}
            onChange={(reinstatements) =>
              sectionEditor.updateField("reinstatements", reinstatements)
            }
          />
        </TabPanel>

        {/* Contract Parties Tab */}
        <TabPanel>
          <ContractPartiesEditor
            contractParties={section.contractParties || []}
            onChange={(contractParties) =>
              sectionEditor.updateField("contractParties", contractParties)
            }
          />
        </TabPanel>

        {/* Contract Market Tab */}
        <TabPanel>
          <ContractMarketEditor
            contractMarket={section.contractMarket || []}
            onChange={(contractMarket) =>
              sectionEditor.updateField("contractMarket", contractMarket)
            }
          />
        </TabPanel>

        {/* Clauses Tab */}
        <TabPanel>
          <ClausesEditor
            clauses={section.clauses || []}
            onChange={(clauses) => sectionEditor.updateField("clauses", clauses)}
          />
        </TabPanel>

        {/* Schedules Tab */}
        <TabPanel>
          <SchedulesEditor
            schedules={section.schedules || []}
            onChange={(schedules) => sectionEditor.updateField("schedules", schedules)}
          />
        </TabPanel>

        {/* Regulatory Provisions Tab */}
        <TabPanel>
          <RegulatoryProvisionsEditor
            regulatoryProvisions={section.regulatoryProvisions || []}
            onChange={(regulatoryProvisions) =>
              sectionEditor.updateField("regulatoryProvisions", regulatoryProvisions)
            }
          />
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};

// Main Component
// recordType "contract": edits the contract's contractData (mrcai.grlc v1).
// recordType "deal": deals no longer carry placement terms, so the editor
// can be used but not saved until it's decided where those terms live.
const GRLCPlacementEditorRefactored = ({ contractInstance, productTemplate, recordType = "contract" }) => {
  const isDeal = recordType === "deal";
  const termsSupported = isDeal || isPlacementSupported(contractInstance);
  const canSave = !isDeal && termsSupported;
  const toast = useToast();
  const navigate = useNavigate();

  // Initialize state with default structure
  const [placement, dispatch] = usePlacementReducer({
    placingStageCode: "build",
    placingTransactionFunctionCode: "submission",
    contract: {
      _id: "",
      name: "",
      type: "",
      typeDescription: "",
      natureCode: "",
      treatyFacCode: "",
      accountingBasis: "",
    },
    sections: [],
  });

  // Load data from contractInstance on mount or when it changes
  useEffect(() => {
    const grlc = isDeal ? null : readPlacement(contractInstance);
    if (!grlc) return;

    // Transform and normalize the data
    const normalizedPlacement = {
      placingStageCode: grlc.placingStageCode || "build",
      placingTransactionFunctionCode:
        grlc.placingTransactionFunctionCode || "submission",
      contract: {
        _id: contractInstance._id || "",
        name: contractInstance.name || "",
        type: grlc.contract?.type || "",
        typeDescription: grlc.contract?.typeDescription || "",
        natureCode: grlc.contract?.natureCode || "",
        treatyFacCode: grlc.contract?.treatyFacCode || "",
        accountingBasis: grlc.contract?.accountingBasis || "",
      },
      sections: (grlc.sections || []).map((section) => ({
        _id: section._id || crypto.randomUUID(),
        name: section.name || "",
        description: section.description || "",
        contractReportingLevelCode: section.contractReportingLevelCode || "",
        riskOrInterestDescription: section.riskOrInterestDescription || "",
        type: section.type || "",
        natureCode: section.natureCode || "",
        treatyFacCode: section.treatyFacCode || "",
        coverType: section.coverType || "",
        coverOperatingBasis: section.coverOperatingBasis || "",
        contractAmountsCcy: section.contractAmountsCcy || "",
        references: section.references || [],
        period: {
          id: section.period?.id || "",
          statusCode: section.period?.statusCode || "",
          description: section.period?.description || "",
          scopeCode: section.period?.scopeCode || "",
          startDateTime: {
            value: section.period?.startDateTime?.value || "",
            timeZoneCode: section.period?.startDateTime?.timeZoneCode || "",
          },
          endDateTime: {
            value: section.period?.endDateTime?.value || "",
            timeZoneCode: section.period?.endDateTime?.timeZoneCode || "",
          },
          underwritingYear: section.period?.underwritingYear || "",
          classes: section.period?.classes || [],
          perils: section.period?.perils || [],
          locations: section.period?.locations || [],
          coverages: section.period?.coverages || [],
          deductibles: section.period?.deductibles || [],
        },
        premiums: section.premiums || [],
        premiumAdjustment: section.premiumAdjustment || {},
        taxes: section.taxes || [],
        commissions: section.commissions || [],
        otherDeductions: section.otherDeductions || [],
        brokerages: section.brokerages || [],
        reinstatements: section.reinstatements || [],
        contractParties: section.contractParties || [],
        contractMarket: section.contractMarket || [],
        clauses: section.clauses || [],
        schedules: section.schedules || [],
        regulatoryProvisions: section.regulatoryProvisions || [],
      })),
    };

    dispatch({
      type: PLACEMENT_ACTIONS.SET_PLACEMENT,
      payload: normalizedPlacement,
    });
  }, [contractInstance, productTemplate, isDeal]);

  const handleSave = async () => {
    if (!contractInstance || !contractInstance._id) {
      toast({ title: "Missing contract ID. Cannot save.", status: "error" });
      return;
    }

    if (!canSave) {
      toast({
        title: "Placement not saved",
        description: isDeal
          ? "Deals no longer hold placement terms."
          : "This contract's terms are in a format this editor doesn't support.",
        status: "warning",
      });
      return;
    }

    // Merge onto the stored terms so fields this editor doesn't map are kept
    const fullPayload = withPlacement(contractInstance, {
      ...readPlacement(contractInstance),
      ...placement,
    });

    try {
      const response = await fetch(
        `https://poc.mrc.ai/api/contracts/${contractInstance._id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(fullPayload),
        }
      );

      if (!response.ok) {
        throw await errorFromResponse(response, "Save failed");
      }

      toast({ title: "Contract saved successfully", status: "success" });
    } catch (err) {
      toast({
        title: err.code === "READ_ONLY" ? "Read only" : "Error saving contract",
        description: err.code === "READ_ONLY"
          ? err.message || "Your organisation doesn't own this contract, so it can't be changed."
          : err.message,
        status: "error",
      });
      console.error("Save error:", err);
    }
  };

  const updateContractField = (field, value) => {
    dispatch({
      type: PLACEMENT_ACTIONS.UPDATE_CONTRACT_FIELD,
      field,
      value,
    });
  };

  const addSection = () => {
    const newSection = {
      _id: crypto.randomUUID(),
      name: "New Section",
      description: "",
      contractReportingLevelCode: "",
      riskOrInterestDescription: "",
      type: "",
      natureCode: "",
      treatyFacCode: "",
      coverType: "",
      coverOperatingBasis: "",
      contractAmountsCcy: "",
      references: [],
      period: {
        id: crypto.randomUUID(),
        statusCode: "",
        description: "",
        classes: [],
        perils: [],
        locations: [],
        coverages: [],
        deductibles: [],
      },
      premiums: [],
      taxes: [],
      commissions: [],
      otherDeductions: [],
      brokerages: [],
      reinstatements: [],
      contractParties: [],
      contractMarket: [],
      clauses: [],
      schedules: [],
      regulatoryProvisions: [],
    };

    dispatch({
      type: PLACEMENT_ACTIONS.SET_PLACEMENT,
      payload: {
        ...placement,
        sections: [...placement.sections, newSection],
      },
    });
  };

  return (
    <Box p={6} maxWidth="100%" mx="auto">
      <HStack mb={4}>
        <Tooltip
          label="Back to the Product Dashboard"
          aria-label="Product Dashboard tooltip">
          <IconButton
            icon={<HiOutlineArrowNarrowLeft />}
            size="md"
            onClick={() => window.history.back()}
          />
        </Tooltip>
        <Heading size="lg">Edit Placement Data (Refactored)</Heading>
        <Tooltip label="Save Placement Data" aria-label="Save tooltip">
          <IconButton icon={<HiOutlineSave />} size="md" onClick={handleSave} isDisabled={!canSave} />
        </Tooltip>
      </HStack>

      {!canSave && (
        <Alert status="warning" my={3}>
          <AlertIcon />
          {isDeal
            ? "Deals no longer hold placement terms, so changes here can't be saved."
            : "This contract's terms are in a format this editor doesn't support, so they can't be edited here."}
        </Alert>
      )}

      <Flex gap={6}>
        <Box mb={4} flex="3">
          <Tabs orientation="horizontal" variant="line" colorScheme="linkedin">
            <TabList>
              <Tab>Contract Header</Tab>
              <Tab>References</Tab>
            </TabList>
            <TabPanels>
              <TabPanel>
                <Box borderWidth="1px" borderRadius="md" p={4}>
                  <Heading size="md" mb={4}>Contract Information</Heading>
                  <FormControl mb={4}>
                    <FormLabel>Name</FormLabel>
                    <Input
                      value={placement.contract.name || ""}
                      onChange={(e) =>
                        updateContractField("name", e.target.value)
                      }
                    />
                  </FormControl>
                  {/* Add more contract fields as needed */}
                </Box>
              </TabPanel>

              <TabPanel>
                <Heading size="md" mb={4}>References</Heading>
                <ReferencesEditor
                  filter="#Contract"
                  references={placement.contract.references || []}
                  onChange={(refs) => updateContractField("references", refs)}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>

        <Box mb={4} flex="9">
          <HStack mb={4}>
            <Heading size="md">Sections</Heading>
            <IconButton
              icon={<HiOutlinePlus />}
              size="md"
              aria-label="Add Section"
              onClick={addSection}
            />
          </HStack>

          <Tabs orientation="horizontal" variant="enclosed" colorScheme="linkedin">
            <TabList>
              {placement.sections.map((section, idx) => (
                <Tab key={section._id}>{section.name || `Section ${idx + 1}`}</Tab>
              ))}
            </TabList>
            <TabPanels>
              {placement.sections.map((section, idx) => (
                <TabPanel key={section._id}>
                  <SectionEditor
                    section={section}
                    sectionIndex={idx}
                    dispatch={dispatch}
                  />
                </TabPanel>
              ))}
            </TabPanels>
          </Tabs>
        </Box>
      </Flex>
    </Box>
  );
};

export default GRLCPlacementEditorRefactored;
